import React, { useContext, useMemo, useState } from 'react';
import {
  AppBar,
  Avatar,
  Badge,
  Box,
  Button,
  Chip,
  IconButton,
  InputBase,
  Stack,
  Toolbar,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import Brightness4Icon from '@mui/icons-material/Brightness4';
import Brightness5Icon from '@mui/icons-material/Brightness5';
import Menu from './Menu';
import MobileBottomNav from './MobileBottomNav';
import { ColorModeContext } from '../styles/theme';
import { BRANDING } from '../config/branding';

const DRAWER_WIDTH = 264;

const Layout = ({ children }) => {
  const theme = useTheme();
  const colorMode = useContext(ColorModeContext);
  const mdUp = useMediaQuery(theme.breakpoints.up('lg'));
  const isPhone = useMediaQuery(theme.breakpoints.down('sm'));
  const [drawerOpen, setDrawerOpen] = useState(false);
  const appBarHeight = isPhone ? 64 : 80;

  const surfaceGradient = useMemo(
    () =>
      theme.palette.mode === 'light'
        ? 'linear-gradient(135deg, rgba(37,99,235,0.10), rgba(124,58,237,0.08))'
        : 'linear-gradient(135deg, rgba(91,142,255,0.22), rgba(192,132,252,0.12))',
    [theme.palette.mode]
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'background.default' }}>
      <AppBar
        position="fixed"
        sx={{
          zIndex: (t) => t.zIndex.drawer + 1,
          backgroundImage:
            theme.palette.mode === 'light'
              ? 'linear-gradient(90deg, #2563eb, #7c3aed)'
              : 'linear-gradient(90deg, #0f172a, #312e81)',
        }}
      >
        <Toolbar
          sx={{
            minHeight: appBarHeight,
            gap: 2,
            display: 'flex',
            justifyContent: 'space-between',
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Button color="inherit" variant="outlined" aria-label="Zona de Ráfaga" aria-expanded={drawerOpen}
              aria-controls="menu-principal" onClick={() => setDrawerOpen((prev) => !prev)}
              startIcon={drawerOpen ? <CloseIcon /> : <MenuIcon />}
              sx={{textTransform:'none',fontWeight:700,fontSize:{xs:13,sm:14},borderRadius:2,whiteSpace:'nowrap',
                borderColor:'rgba(255,255,255,0.35)',bgcolor:drawerOpen ? 'rgba(255,255,255,0.24)' : 'rgba(255,255,255,0.1)',
                '&:hover':{bgcolor:'rgba(255,255,255,0.24)',borderColor:'rgba(255,255,255,0.6)'}}}>
              Zona de Ráfaga
            </Button>
            <Box sx={{display:{xs:'none',sm:'block'}}}>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                {BRANDING.companyName}
              </Typography>
              <Chip
                size="small"
                label="Panel operativo"
                sx={{
                  bgcolor: 'rgba(255,255,255,0.24)',
                  color: 'common.white',
                  mt: 0.5,
                  fontSize: 12,
                }}
              />
            </Box>
          </Stack>

          <Box
            sx={{
              flex: 1,
              maxWidth: 420,
              display: { xs: 'none', md: 'flex' },
              alignItems: 'center',
              bgcolor: 'rgba(255,255,255,0.16)',
              borderRadius: 999,
              px: 2,
              py: 0.5,
            }}
          >
            <SearchIcon sx={{ color: 'common.white', opacity: 0.85, mr: 1 }} />
            <InputBase
              placeholder="Buscar acciones, clientes o modulos"
              sx={{ color: 'common.white', flex: 1 }}
            />
          </Box>

          <Stack direction="row" spacing={1} alignItems="center">
            <IconButton
              color="inherit"
              onClick={colorMode.toggleColorMode}
              sx={{
                bgcolor: 'rgba(255,255,255,0.16)',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.24)' },
              }}
            >
              {theme.palette.mode === 'dark' ? <Brightness5Icon /> : <Brightness4Icon />}
            </IconButton>
            <Badge color="secondary" variant="dot" overlap="circular">
              <IconButton
                color="inherit"
                sx={{
                  bgcolor: 'rgba(255,255,255,0.16)',
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.24)' },
                }}
              >
                <NotificationsNoneIcon />
              </IconButton>
            </Badge>
            <Avatar
              sx={{
                width: 38,
                height: 38,
                fontSize: 16,
                bgcolor: 'common.white',
                color: 'primary.main',
                fontWeight: 700,
              }}
            >
              MD
            </Avatar>
          </Stack>
        </Toolbar>
      </AppBar>

      <Menu
        variant={mdUp ? 'persistent' : 'temporary'}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        width={DRAWER_WIDTH}
        appBarHeight={appBarHeight}
        onNavigateClose={() => setDrawerOpen(false)}
      />

      <Box
        component="main"
        sx={{
          flex: 1,
          minWidth: 0,
          pt: `${appBarHeight + 16}px`,
          pb: isPhone ? 'calc(16px + 62px + max(env(safe-area-inset-bottom), 8px))' : 4,
          px: { xs: 2, md: 4 },
          minHeight: '100vh',
          backgroundImage: surfaceGradient,
        }}
      >
        <Box
          sx={{
            minHeight: isPhone
              ? 'calc(100vh - 120px - 62px - max(env(safe-area-inset-bottom), 8px))'
              : 'calc(100vh - 120px)',
            backdropFilter: 'blur(12px)',
            borderRadius: 4,
          }}
        >
          {children}
        </Box>
      </Box>

      <MobileBottomNav onOpenMenu={() => setDrawerOpen(true)} />
    </Box>
  );
};

export default Layout;
